/**
 * Onda 0 do ADR-034 (Camada 1) — módulo de dado puro, mesma disciplina de
 * `nav-registry.ts`: zero import de react/react-dom ou de páginas, importável
 * estaticamente sem entrar no bundle eager.
 *
 * Escopo desta onda: 100% aditivo/mecânico, zero mudança de comportamento em
 * produção. `RESOURCES` modela TODOS os grupos `(categoria, subcategoria,
 * nome)` hoje existentes em `PERMISSIONS`
 * (`backend/apps/permissions/management/commands/seed_permissions.py`) e
 * `ROUTES` modela TODAS as rotas reais de `frontend/src/main.ts` (exceto
 * redirects internos e o handler de 404). Nem `main.ts`, nem
 * `nav-registry.ts`, nem `sitemap.tsx`, nem `PERMISSIONS` passam a ler daqui
 * ainda — isso é a Implementação completa, fora desta onda. `synthesizeLabel`
 * já validado no PoC restrito anterior, não muda.
 */

/** Ação sobre um recurso — mesmo domínio de `Opcao` em `backend/apps/permissions/models.py`. */
export type Opcao = "view" | "edit" | "create" | "delete";

/** Mesma semântica de `subcategoria` em `FeaturePermission` (área user vs. área admin). */
export type Subcategoria = "admin" | "user";

/**
 * 1 entrada por recurso — cobre as N `opcoes` de uma vez (ADR-034, Camada 1).
 * `label_prefix` e `nome_label` são decisão editorial humana independente,
 * não deriváveis um do outro (ex.: `documentos-categorias` abaixo).
 */
export interface ResourceEntry {
  categoria: string;
  subcategoria: Subcategoria;
  nome: string;
  opcoes: Opcao[];
  label_prefix: string;
  categoria_label: string;
  nome_label: string;
}

/**
 * Union do guard de rota (ADR-034, Camada 1). `key` de `"resource"` referencia
 * um `ResourceEntry` pela combinação `categoria.subcategoria.nome` (sem
 * opcao — a opcao é campo próprio). `keys` de `"resource-any"` são codenames
 * completos (`categoria.subcategoria.nome.opcao`), cobrindo o
 * `guarded([...codenames])` que já existe em algumas rotas.
 */
export type Guard =
  | { type: "resource"; key: string; opcao: Opcao }
  | { type: "resource-any"; keys: string[] }
  | { type: "superadmin" }
  | { type: "none" };

/**
 * 1 entrada por rota real da SPA. `path` aceita `RegExp` porque várias rotas
 * reais de `main.ts` usam path regex (segmento dinâmico de id, alternância de
 * sufixo `novo|editar` etc.) — mesmo tipo de `Route["path"]` em
 * `frontend/src/router.ts`.
 */
export interface RouteEntry {
  path: string | RegExp;
  label: string;
  description: string;
  icon: string;
  guard: Guard;
}

/**
 * Algoritmo de síntese de label — paridade byte a byte com
 * `FeaturePermissionViewSet.bulk_create` (`backend/apps/permissions/views.py:142`,
 * `f"{label_prefix} — {opcao.title()}"`). `capitalize` aqui só precisa cobrir
 * o domínio fechado de `Opcao` (palavras únicas, minúsculas, ASCII) — não é
 * uma reimplementação geral de `str.title()` do Python.
 */
function capitalize(opcao: Opcao): string {
  return opcao.charAt(0).toUpperCase() + opcao.slice(1);
}

export function synthesizeLabel(labelPrefix: string, opcao: Opcao): string {
  return `${labelPrefix} — ${capitalize(opcao)}`;
}

/**
 * Onda 1 do ADR-034 (Camada 1) — deriva o codename completo de um recurso
 * (`categoria.subcategoria.nome.opcao`) a partir da chave curta
 * (`categoria.subcategoria.nome`, mesmo `guard.key` de `{ type: "resource" }`)
 * e da opção. Byte-idêntico ao literal que `guarded(codename, render)`/
 * `perm: codename` em `main.ts`/`nav-registry.ts` já usavam — verificado em
 * `resource-catalog.test.ts` contra os literais reais removidos nesta onda.
 */
export function codename(key: string, opcao: Opcao): string {
  return `${key}.${opcao}`;
}

/**
 * Mesma derivação de `codename`, para o caso `guarded([...codenames])`
 * (`guard.type === "resource-any"`). O único caso real que existiu (rota
 * "Documentos", `gerenciamento.admin.documentos`/
 * `parametrizacoes.admin.documentos-categorias`) foi reduzido a resource único
 * pelo Card 868m1ja6b — "Categorias" virou rota própria com guard só de
 * `parametrizacoes.admin.documentos-categorias` (ver
 * `admin-documentos-categorias.tsx`). Nenhuma rota usa `resource-any` hoje;
 * função mantida para a próxima que precisar de OR entre 2+ famílias — assume
 * a mesma `opcao` ("view") para todas as chaves; uma rota futura com opções
 * diferentes por chave exigiria pares explícitos `[key, opcao][]`, resolvido
 * quando/se aparecer.
 */
export function codenames(keys: string[], opcao: Opcao): string[] {
  return keys.map((key) => codename(key, opcao));
}

/**
 * Busca uma `RouteEntry` em `ROUTES` por um `path` literal — reuso no Mapa do
 * Site (`sitemap.tsx`) para derivar label/description/icon a partir da mesma
 * fonte que já guarda a rota, em vez de duplicar esses campos manualmente.
 *
 * Duas estratégias, nesta ordem:
 * 1. Igualdade exata contra `RouteEntry.path` quando este é `string`
 *    (comportamento original da Onda 0/1 — inalterado).
 * 2. `RegExp.test(path)` contra `RouteEntry.path` quando este é `RegExp`
 *    (ADR-034, Onda 5) — cobre as rotas `/app/*` de segmento dinâmico opcional
 *    (ex.: `/^\/app\/dashboard\/?$/`), que uma igualdade de string nunca
 *    casaria. Mais de uma entrada regex casando o mesmo `path` é tratado como
 *    erro explícito (não deveria acontecer hoje — nenhuma rota se sobrepõe).
 */
export function routeEntryFor(path: string): RouteEntry {
  const exact = ROUTES.find((r) => typeof r.path === "string" && r.path === path);
  if (exact) {
    return exact;
  }

  const regexMatches = ROUTES.filter((r) => r.path instanceof RegExp && r.path.test(path));
  if (regexMatches.length > 1) {
    throw new Error(
      `resource-catalog: mais de uma RouteEntry (regex) casou para o path "${path}": ${regexMatches
        .map((r) => r.label)
        .join(", ")}`,
    );
  }
  if (regexMatches.length === 1) {
    return regexMatches[0];
  }

  throw new Error(`resource-catalog: nenhuma RouteEntry encontrada para o path "${path}"`);
}

/**
 * 27 recursos reais — 1 por grupo único `(categoria, subcategoria, nome)` de
 * `PERMISSIONS` (seed_permissions.py), na mesma ordem/agrupamento por seção
 * do arquivo original. `label_prefix` de cada um foi validado batendo
 * `synthesizeLabel(label_prefix, opcao)` contra o `label` real de
 * `PERMISSIONS` para TODAS as opções do recurso — nenhuma exceção encontrada
 * (todos os 62 registros originais seguem o padrão `"{label_prefix} —
 * {Opcao.title()}"`). Os 2 registros de `configuracao.admin.api-docs.view`/
 * `.sitemap.view` citados no comentário final de `seed_permissions.py` não
 * entram aqui — já removidos do catálogo (viraram `guardedSuperadmin`, fora
 * de `PERMISSIONS`).
 *
 * `midia.admin.biblioteca` (4 opções) foi adicionado pelo plano "Midia e
 * Configuracoes Gerais" (Fase 1) — recurso novo, não parte do lote original
 * de 62 linhas; concedido ao GroupProfile default de admin por migração de
 * dados, não pelo array estático `PERMISSIONS`.
 */
export const RESOURCES: ResourceEntry[] = [
  // ── cotista / user — funcionalidades gerais do proprietário ───────────────
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "dashboard",
    opcoes: ["view"],
    label_prefix: "Dashboard",
    categoria_label: "Parametrizações",
    nome_label: "Dashboard",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "boletos",
    opcoes: ["view"],
    label_prefix: "Boletos",
    categoria_label: "Parametrizações",
    nome_label: "Boletos",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "documentos",
    opcoes: ["view"],
    label_prefix: "Documentos",
    categoria_label: "Parametrizações",
    nome_label: "Documentos",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "empreendimentos",
    opcoes: ["view"],
    label_prefix: "Meus Empreendimentos",
    categoria_label: "Parametrizações",
    nome_label: "Meus Empreendimentos",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "financeiro",
    opcoes: ["view"],
    label_prefix: "Extrato Financeiro",
    categoria_label: "Parametrizações",
    nome_label: "Extrato Financeiro",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "perfil",
    opcoes: ["view"],
    label_prefix: "Meu Perfil",
    categoria_label: "Parametrizações",
    nome_label: "Meu Perfil",
  },
  {
    categoria: "cotista",
    subcategoria: "user",
    nome: "comunicados",
    opcoes: ["view"],
    label_prefix: "Comunicados",
    categoria_label: "Parametrizações",
    nome_label: "Comunicados",
  },
  // ── condominio / user — área de condomínio ────────────────────────────────
  {
    categoria: "condominio",
    subcategoria: "user",
    nome: "condominio",
    opcoes: ["view"],
    label_prefix: "Condomínio — Visão Geral",
    categoria_label: "Condomínio",
    nome_label: "Condomínio — Visão Geral",
  },
  {
    categoria: "condominio",
    subcategoria: "user",
    nome: "cotas",
    opcoes: ["view"],
    label_prefix: "Cotas e Unidades",
    categoria_label: "Condomínio",
    nome_label: "Cotas e Unidades",
  },
  {
    categoria: "condominio",
    subcategoria: "user",
    nome: "prestacao-contas",
    opcoes: ["view"],
    label_prefix: "Prestação de Contas",
    categoria_label: "Condomínio",
    nome_label: "Prestação de Contas",
  },
  // ── hotelaria / user — área hoteleira ─────────────────────────────────────
  {
    categoria: "hotelaria",
    subcategoria: "user",
    nome: "pool-hoteleiro",
    opcoes: ["view"],
    label_prefix: "Pool de Hotelaria — Visão Geral",
    categoria_label: "Hotelaria",
    nome_label: "Pool de Hotelaria — Visão Geral",
  },
  {
    categoria: "hotelaria",
    subcategoria: "user",
    nome: "ocupacao",
    opcoes: ["view"],
    label_prefix: "Ocupação",
    categoria_label: "Hotelaria",
    nome_label: "Ocupação",
  },
  {
    categoria: "hotelaria",
    subcategoria: "user",
    nome: "reservas",
    opcoes: ["view"],
    label_prefix: "Reservas",
    categoria_label: "Hotelaria",
    nome_label: "Reservas",
  },
  {
    categoria: "hotelaria",
    subcategoria: "user",
    nome: "intercambio",
    opcoes: ["view"],
    label_prefix: "Intercâmbio",
    categoria_label: "Hotelaria",
    nome_label: "Intercâmbio",
  },
  // ── hotelaria / admin — controle das reservas feitas pela plataforma ───────
  {
    categoria: "hotelaria",
    subcategoria: "admin",
    nome: "reservas",
    opcoes: ["view", "edit"],
    label_prefix: "Controle de Reservas (Admin)",
    categoria_label: "Hotelaria",
    nome_label: "Controle de Reservas",
  },
  // ── parametrizacoes / admin — configuração de boletos (área admin) ─────────
  {
    categoria: "parametrizacoes",
    subcategoria: "admin",
    nome: "boletos",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Boletos (Admin)",
    categoria_label: "Parametrizações",
    nome_label: "Boletos",
  },
  // ── parametrizacoes / admin — enriquecimento de empreendimentos (área admin)
  {
    categoria: "parametrizacoes",
    subcategoria: "admin",
    nome: "empreendimentos",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Empreendimentos (Admin)",
    categoria_label: "Parametrizações",
    nome_label: "Empreendimentos",
  },
  // ── gestao / admin — gestão de usuários (área admin) ──────────────────────
  {
    categoria: "gestao",
    subcategoria: "admin",
    nome: "usuarios",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Usuários (Admin)",
    categoria_label: "Gestão",
    nome_label: "Usuários",
  },
  // ── gestao / admin — catálogo de permissões (área admin, leitura) ─────────
  {
    categoria: "gestao",
    subcategoria: "admin",
    nome: "catalogo",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Permissões (avançado) (Admin)",
    categoria_label: "Gestão",
    nome_label: "Permissões (avançado)",
  },
  // ── gestao / admin — grupos de permissão (área admin) ─────────────────────
  {
    categoria: "gestao",
    subcategoria: "admin",
    nome: "grupos",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Grupos de Permissão (Admin)",
    categoria_label: "Gestão",
    nome_label: "Grupos de Permissão",
  },
  // ── parametrizacoes / admin — regras do portal do proprietário (reservas) ──
  {
    categoria: "parametrizacoes",
    subcategoria: "admin",
    nome: "portal-regras",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Regras eSolution Portal (Admin)",
    categoria_label: "Parametrizações",
    nome_label: "Regras eSolution Portal",
  },
  // ── parametrizacoes / admin — fila de aprovação de depósito no pool ────────
  {
    categoria: "parametrizacoes",
    subcategoria: "admin",
    nome: "pool-aprovacao",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Pool Hoteleiro (Admin)",
    categoria_label: "Parametrizações",
    nome_label: "Pool Hoteleiro",
  },
  // ── gerenciamento / admin — histórico de disparos de e-mail ────────────────
  {
    categoria: "gerenciamento",
    subcategoria: "admin",
    nome: "emails",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Disparos de E-mail (Admin)",
    categoria_label: "Gerenciamento",
    nome_label: "Disparos de E-mail",
  },
  // ── gerenciamento / admin — comunicados aos cotistas ───────────────────────
  {
    categoria: "gerenciamento",
    subcategoria: "admin",
    nome: "comunicados",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Comunicados (Admin)",
    categoria_label: "Gerenciamento",
    nome_label: "Comunicados",
  },
  // ── gerenciamento / admin — banners do dashboard do cotista ────────────────
  {
    categoria: "gerenciamento",
    subcategoria: "admin",
    nome: "banners",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Banners (Admin)",
    categoria_label: "Gerenciamento",
    nome_label: "Banners",
  },
  // ── gerenciamento / admin — CRUD de remetentes de e-mail (ADR-014) ─────────
  {
    categoria: "gerenciamento",
    subcategoria: "admin",
    nome: "remetentes-email",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Remetentes de E-mail (Admin)",
    categoria_label: "Gerenciamento",
    nome_label: "Remetentes de E-mail",
  },
  // ── gerenciamento / admin — Documentos e Informes de IR (área admin) ───────
  {
    categoria: "gerenciamento",
    subcategoria: "admin",
    nome: "documentos",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Documentos (Admin)",
    categoria_label: "Gerenciamento",
    nome_label: "Documentos",
  },
  // ── parametrizacoes / admin — taxonomia de categorias de documento (ADR-031).
  // Caso citado no ADR-034 como exemplo de label_prefix/nome_label divergentes
  // ("Categorias de Documento (Admin)" × "Documentos — Categorias"); desde
  // 02-10-2026 ambos seguem o título da sidebar ("Parametrização de
  // Documentos") — ver `nav-permission-catalog-consistency.test.ts`. Antes,
  // diferentes — não deriváveis um do outro.
  {
    categoria: "parametrizacoes",
    subcategoria: "admin",
    nome: "documentos-categorias",
    opcoes: ["view", "edit", "create", "delete"],
    label_prefix: "Parametrização de Documentos (Admin)",
    categoria_label: "Parametrizações",
    nome_label: "Parametrização de Documentos",
  },
  // ── midia / admin — Biblioteca de Mídia (plano "Midia e Configuracoes
  // Gerais", Fase 1). Admin (perfil padrão) já vê/sobe/apaga por decisão do
  // dono — concessão ao GroupProfile default via migração de dados
  // (`backend/apps/permissions/migrations/0013_grant_midia_biblioteca_
  // default_admin.py`), não pelo seed estático de PERMISSIONS.
  {
    categoria: "midia",
    subcategoria: "admin",
    nome: "biblioteca",
    opcoes: ["view", "create", "edit", "delete"],
    label_prefix: "Biblioteca de Mídia (Admin)",
    categoria_label: "Mídia",
    nome_label: "Biblioteca de Mídia",
  },
];

/**
 * 70 rotas reais de `frontend/src/main.ts` (o array `routes`), excluídos só
 * os 6 `defineInternalRedirect(...)` (redirect puro, sem página própria) e o
 * handler de 404 (`renderNotFoundReact`, passado a `startRouter` fora do
 * array `routes`). `guard` reflete exatamente o que a rota usa hoje em
 * `main.ts` — nenhuma mudança de comportamento.
 *
 * Plano "Mídia e uploads por módulo" (Fase 5, 11-09-2026): a rota única
 * "Customização da Instância" virou 3 (Instância/Layout/White-label,
 * `pages/configuracoes/`) + `defineInternalRedirect` de
 * `/painel/superadmin/customizacao/`; `/painel/admin/configuracoes/`
 * (coming-soon) virou `defineInternalRedirect` para `/painel/admin/
 * integracoes/`; entraram as 2 rotas de Mídia (Biblioteca/Upload). Net:
 * 67 - 1 (customizacao) - 1 (admin/configuracoes) + 3 (Geral) + 2 (Mídia) = 70;
 * redirects internos: os 4 pré-existentes + 2 novos = 6.
 *
 * `label`/`description`/`icon` reaproveitados de `SITE_MAP`
 * (`frontend/src/react/pages/sitemap.tsx`) quando a rota já está listada lá
 * por path exato. Rotas marcadas "SITE_MAP gap" abaixo são rotas reais sem
 * entrada correspondente em `SITE_MAP` hoje (lacuna já existente, não
 * introduzida nesta onda) — label/description derivados do `key`/breadcrumb
 * da rota em `main.ts`, mantidos deliberadamente curtos.
 */
export const ROUTES: RouteEntry[] = [
  // ── Superadmin area ────────────────────────────────────────────────────
  // main.ts:221 — guardedSuperadmin(...). SITE_MAP: "API Docs".
  {
    path: "/painel/admin/api-docs/",
    label: "Documentação da API",
    description: "Documentação da API (OpenAPI).",
    icon: "book-open",
    guard: { type: "superadmin" },
  },
  // main.ts:252 — sem guard (double-mounted com "admin-catalogo", achado
  // documentado no BACKLOG-HARDENING-PERMISSOES.md). SITE_MAP: "Permissões (catálogo)".
  {
    path: "/painel/admin/permissoes/catalogo/",
    label: "Permissões (catálogo)",
    description: "Catálogo de recursos e ações — config avançada.",
    icon: "key",
    guard: { type: "none" },
  },
  // main.ts:277 — sem guard. SITE_MAP gap (só a rota de lista está listada).
  {
    path: "/painel/admin/permissoes/catalogo/editar/",
    label: "Catálogo de Permissões — Editar",
    description: "Criação/edição de item do catálogo de permissões.",
    icon: "key",
    guard: { type: "none" },
  },
  // main.ts:306 — sem guard. SITE_MAP: "Perfis de Acesso".
  {
    path: "/painel/admin/permissoes/perfis/",
    label: "Perfis de Acesso",
    description: "Perfis reutilizáveis de permissões (grupos).",
    icon: "shield",
    guard: { type: "none" },
  },
  // main.ts:326 — sem guard. SITE_MAP gap (path regex, sem entrada exata).
  {
    path: /^\/painel\/admin\/permissoes\/usuarios\/(admin|user)?\/?$/,
    label: "Usuários",
    description: "Gerenciamento de usuários e vínculo a perfis.",
    icon: "users",
    guard: { type: "none" },
  },
  // main.ts:350 — sem guard. SITE_MAP gap (form dinâmico, sem entrada própria).
  {
    path: /^\/painel\/admin\/permissoes\/usuarios\/(novo|\d+\/editar)\/?$/,
    label: "Usuários — Editar",
    description: "Criação/edição de usuário (zona Acessos & Permissões).",
    icon: "users",
    guard: { type: "none" },
  },
  // main.ts:373 — sem guard. SITE_MAP gap (form dinâmico, sem entrada própria).
  {
    path: /^\/painel\/admin\/permissoes\/perfis\/(novo|\d+\/editar)\/?$/,
    label: "Perfis de Acesso — Editar",
    description: "Criação/edição de perfil de acesso.",
    icon: "shield",
    guard: { type: "none" },
  },
  // main.ts:402 — sem guard. SITE_MAP: "Auditoria de Acesso" (seção Acessos & Permissões).
  {
    path: "/painel/admin/permissoes/acesso/",
    label: "Auditoria de Acesso",
    description: "Quais perfis e usuários administram cada recurso.",
    icon: "circle-check",
    guard: { type: "none" },
  },
  // main.ts:422 — sem guard. SITE_MAP: "Usuários" (seção Acessos & Permissões).
  {
    path: "/painel/superadmin/permissoes/usuarios/",
    label: "Usuários",
    description: "Gerenciamento de usuários e vínculo a perfis.",
    icon: "users",
    guard: { type: "none" },
  },
  // main.ts:450 — sem guard. SITE_MAP gap (form dinâmico, sem entrada própria).
  {
    path: /^\/painel\/superadmin\/permissoes\/usuarios\/(novo|\d+\/editar)\/?$/,
    label: "Usuários — Editar",
    description: "Criação/edição de usuário (zona superadmin).",
    icon: "users",
    guard: { type: "none" },
  },
  // main.ts — guardedSuperadmin(...) (ADR-042, split Fase 6 do plano "Mídia e
  // uploads por módulo"). SITE_MAP: "Instância" (Configurações › Geral).
  // `/painel/superadmin/customizacao/` (URL antiga) virou
  // `defineInternalRedirect` para esta rota — não é mais RouteEntry própria.
  {
    path: "/painel/superadmin/instancia/",
    label: "Instância",
    description: "Nome da empresa, título, descrição e limite de upload da instância.",
    icon: "building-2",
    guard: { type: "superadmin" },
  },
  // main.ts — guardedSuperadmin(...). SITE_MAP: "Layout" (Configurações › Geral).
  // Absorve a maior parte da antiga "Customização da Instância" (paleta,
  // superfícies, tipografia/forma e navegação).
  {
    path: "/painel/superadmin/layout/",
    label: "Aparência",
    description: "Cores, fontes e menu do portal.",
    icon: "palette",
    guard: { type: "superadmin" },
  },
  // main.ts — guardedSuperadmin(...). SITE_MAP: "White-label" (Configurações › Geral).
  {
    path: "/painel/superadmin/white-label/",
    label: "White-label",
    description: "Marca própria: logo, assinatura do sistema e favicons.",
    icon: "tag",
    guard: { type: "superadmin" },
  },
  // main.ts:480/493 — guardedSuperadmin(...). SITE_MAP: "Sitemap" (renome de
  // "Mapa do Site", plano "Mídia e uploads por módulo", Fase 5 — URL mantida).
  {
    path: "/painel/superadmin/mapa-site/",
    label: "Sitemap",
    description: "Esta página — documentação interna das rotas.",
    icon: "map",
    guard: { type: "superadmin" },
  },
  // main.ts:509 — sem guard. SITE_MAP gap (não listada).
  {
    path: "/painel/superadmin/admin-templates/",
    label: "Templates de Perfil",
    description: "Templates de perfil de acesso para admins.",
    icon: "shield",
    guard: { type: "none" },
  },
  // main.ts:536 — sem guard. SITE_MAP gap (não listada).
  {
    path: "/painel/admin/admin-modulos/",
    label: "Gerenciar Admins",
    description: "Gestão de módulos/admins habilitados por cliente.",
    icon: "users",
    guard: { type: "none" },
  },
  // ── Admin area ─────────────────────────────────────────────────────────
  // main.ts:566 — sem guard. SITE_MAP: "Dashboard" (seção Admin/Backoffice).
  {
    path: "/painel/admin/",
    label: "Dashboard",
    description: "Visão geral do admin do cliente.",
    icon: "layout-dashboard",
    guard: { type: "none" },
  },
  // main.ts:587/596 — guarded("parametrizacoes.admin.empreendimentos.view").
  // SITE_MAP: "Empreendimentos" (seção Admin/Backoffice).
  {
    path: "/painel/admin/empreendimentos/",
    label: "Empreendimentos",
    description: "Empreendimentos no escopo do admin.",
    icon: "building-2",
    guard: { type: "resource", key: "parametrizacoes.admin.empreendimentos", opcao: "view" },
  },
  // main.ts:608/621 — mesmo guard da rota de lista acima. SITE_MAP gap (form dinâmico).
  {
    path: /^\/painel\/admin\/empreendimentos\/\d+\/perfil(?:\/(informacoes|apresentacao|comodidades|localizacao|preview)?)?\/?$/,
    label: "Empreendimentos — Enriquecimento do Perfil",
    description: "Edição do perfil público do empreendimento.",
    icon: "building-2",
    guard: { type: "resource", key: "parametrizacoes.admin.empreendimentos", opcao: "view" },
  },
  // main.ts:636/647 — guarded("gestao.admin.usuarios.view"). SITE_MAP: "Usuários" (Admin/Backoffice).
  {
    path: "/painel/admin/usuarios/",
    label: "Usuários",
    description: "Usuários no escopo do admin.",
    icon: "users",
    guard: { type: "resource", key: "gestao.admin.usuarios", opcao: "view" },
  },
  // main.ts:667/677 — mesmo guard da rota de lista acima. SITE_MAP gap (form dinâmico).
  {
    path: /^\/painel\/admin\/usuarios\/(novo|\d+\/editar)\/?$/,
    label: "Usuários — Editar",
    description: "Criação/edição de usuário (área admin).",
    icon: "users",
    guard: { type: "resource", key: "gestao.admin.usuarios", opcao: "view" },
  },
  // main.ts:691/700 — guarded("gestao.admin.grupos.view"). SITE_MAP: "Perfis de Acesso" (Admin/Backoffice).
  {
    path: "/painel/admin/perfis/",
    label: "Perfis de Acesso",
    description: "Perfis de acesso do admin.",
    icon: "shield",
    guard: { type: "resource", key: "gestao.admin.grupos", opcao: "view" },
  },
  // main.ts:712/726 — guarded("gestao.admin.grupos.view"). SITE_MAP: "Auditoria de Acesso" (Admin/Backoffice).
  {
    path: "/painel/admin/acesso/",
    label: "Auditoria de Acesso",
    description: "Quem tem acesso a cada recurso.",
    icon: "circle-check",
    guard: { type: "resource", key: "gestao.admin.grupos", opcao: "view" },
  },
  // main.ts:739/748 — mesmo guard da rota de lista "/painel/admin/perfis/". SITE_MAP gap (form dinâmico).
  {
    path: /^\/painel\/admin\/perfis\/(novo|\d+\/editar)\/?$/,
    label: "Perfis de Acesso — Editar",
    description: "Criação/edição de perfil de acesso (área admin).",
    icon: "shield",
    guard: { type: "resource", key: "gestao.admin.grupos", opcao: "view" },
  },
  // main.ts:767/778 — guarded("gestao.admin.catalogo.view"). SITE_MAP gap
  // (double-mounted com "/painel/admin/permissoes/catalogo/", que é a única listada).
  {
    path: "/painel/admin/catalogo/",
    label: "Catálogo de Permissões",
    description: "Catálogo de recursos e ações — config avançada (área admin).",
    icon: "key",
    guard: { type: "resource", key: "gestao.admin.catalogo", opcao: "view" },
  },
  // main.ts:790/799 — mesmo guard da rota de lista acima. SITE_MAP gap (form dinâmico).
  {
    path: "/painel/admin/catalogo/editar/",
    label: "Catálogo de Permissões — Editar",
    description: "Criação/edição de item do catálogo (área admin).",
    icon: "key",
    guard: { type: "resource", key: "gestao.admin.catalogo", opcao: "view" },
  },
  // main.ts:814 — defineComingSoonRoute, sem guard. SITE_MAP: "Permissões" (status planejado).
  {
    path: "/painel/admin/permissoes/",
    label: "Permissões",
    description: "Placeholder legado (em breve).",
    icon: "key",
    guard: { type: "none" },
  },
  // guarded("hotelaria.admin.reservas.view") — controle das reservas feitas pela plataforma.
  {
    path: "/painel/admin/reservas/",
    label: "Reservas",
    description: "Controle administrativo das reservas feitas pelo portal.",
    icon: "calendar-check",
    guard: { type: "resource", key: "hotelaria.admin.reservas", opcao: "view" },
  },
  // main.ts:826 — defineComingSoonRoute, sem guard. SITE_MAP: "Ocupação" (status planejado).
  {
    path: "/painel/admin/ocupacao/",
    label: "Ocupação",
    description: "Ocupação hoteleira (em breve).",
    icon: "bed-double",
    guard: { type: "none" },
  },
  // main.ts:833/840 — guarded("parametrizacoes.admin.pool-aprovacao.view"). SITE_MAP: "Pool Hoteleiro".
  {
    path: "/painel/admin/pool/",
    label: "Pool Hoteleiro",
    description: "Fila de aprovação de depósito/retirada e gestão de contratos de pool por cota.",
    icon: "waves",
    guard: { type: "resource", key: "parametrizacoes.admin.pool-aprovacao", opcao: "view" },
  },
  // main.ts:852/859 — guarded("gerenciamento.admin.emails.view"). SITE_MAP gap (não listada).
  {
    path: "/painel/admin/emails/",
    label: "Disparos de E-mail",
    description: "Histórico de disparos de e-mail.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.emails", opcao: "view" },
  },
  // main.ts:871/878 — guarded("gerenciamento.admin.remetentes-email.view"). SITE_MAP: "Remetentes de E-mail".
  {
    path: "/painel/admin/remetentes-email/",
    label: "Remetentes de E-mail",
    description: "Servidores SMTP: autenticação, reservas e pool.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.remetentes-email", opcao: "view" },
  },
  // main.ts:890/895 — guarded("gerenciamento.admin.comunicados.view"). SITE_MAP: "Comunicados" (Admin/Backoffice).
  {
    path: "/painel/admin/comunicados/",
    label: "Comunicados",
    description: "Comunicados aos cotistas.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.comunicados", opcao: "view" },
  },
  // main.ts:907/916 — mesmo guard da rota de lista acima. SITE_MAP gap (sub-rota, sem entrada própria).
  {
    path: /^\/painel\/admin\/comunicados\/relatorio\/?$/,
    label: "Comunicados — Relatório",
    description: "Relatório de envio de um comunicado.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.comunicados", opcao: "view" },
  },
  // main.ts:933/943 — mesmo guard. SITE_MAP gap (sub-rota, sem entrada própria).
  {
    path: /^\/painel\/admin\/comunicados\/novo\/?$/,
    label: "Comunicados — Novo",
    description: "Criação de comunicado.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.comunicados", opcao: "view" },
  },
  // main.ts:960/970 — mesmo guard. SITE_MAP gap (sub-rota, sem entrada própria).
  {
    path: /^\/painel\/admin\/comunicados\/\d+\/editar\/?$/,
    label: "Comunicados — Editar",
    description: "Edição de comunicado.",
    icon: "mail",
    guard: { type: "resource", key: "gerenciamento.admin.comunicados", opcao: "view" },
  },
  // main.ts — guarded("gerenciamento.admin.banners.view"). Banners do dashboard do cotista.
  {
    path: "/painel/admin/banners/",
    label: "Banners",
    description: "Banners do carrossel do dashboard dos cotistas.",
    icon: "image",
    guard: { type: "resource", key: "gerenciamento.admin.banners", opcao: "view" },
  },
  {
    path: /^\/painel\/admin\/banners\/novo\/?$/,
    label: "Banners — Novo",
    description: "Criação de banner.",
    icon: "image",
    guard: { type: "resource", key: "gerenciamento.admin.banners", opcao: "view" },
  },
  {
    path: /^\/painel\/admin\/banners\/\d+\/editar\/?$/,
    label: "Banners — Editar",
    description: "Edição de banner.",
    icon: "image",
    guard: { type: "resource", key: "gerenciamento.admin.banners", opcao: "view" },
  },
  // main.ts:1045 — Card 868m1ja6b: guard reduzido de resource-any (2 famílias)
  // pra resource único — "Categorias" foi extraída pra rota própria (abaixo).
  // SITE_MAP gap (não listada em nenhuma seção).
  {
    path: "/painel/admin/documentos/",
    label: "Documentos",
    description: "Gerenciamento dos arquivos de documento disponibilizados aos cotistas.",
    icon: "file-text",
    guard: { type: "resource", key: "gerenciamento.admin.documentos", opcao: "view" },
  },
  // main.ts:1078 — mesmo guard da rota de lista acima (reduzido junto, Card 868m1ja6b). SITE_MAP gap (form dinâmico).
  {
    path: /^\/painel\/admin\/documentos\/novo\/?$/,
    label: "Documentos — Novo",
    description: "Criação de documento.",
    icon: "file-text",
    guard: { type: "resource", key: "gerenciamento.admin.documentos", opcao: "view" },
  },
  // main.ts:1107 — mesmo guard. SITE_MAP gap (form dinâmico).
  {
    path: /^\/painel\/admin\/documentos\/(\d+)\/editar\/?$/,
    label: "Documentos — Editar",
    description: "Edição de documento.",
    icon: "file-text",
    guard: { type: "resource", key: "gerenciamento.admin.documentos", opcao: "view" },
  },
  // main.ts:1059 — nova rota (Card 868m1ja6b), extraída da aba "Categorias" que
  // antes vivia em "/painel/admin/documentos/". SITE_MAP gap (não listada em
  // nenhuma seção, mesmo padrão da rota "Documentos" acima).
  {
    path: "/painel/admin/documentos-categorias/",
    label: "Parametrização de Documentos",
    description: "Categorias usadas para organizar os documentos disponibilizados aos cotistas.",
    icon: "settings",
    guard: { type: "resource", key: "parametrizacoes.admin.documentos-categorias", opcao: "view" },
  },
  // main.ts:1063 — defineComingSoonRoute, sem guard. SITE_MAP: "Leads" (status planejado).
  {
    path: "/painel/admin/leads/",
    label: "Leads",
    description: "Gestão de leads (em breve).",
    icon: "user-plus",
    guard: { type: "none" },
  },
  // main.ts:1069 — defineComingSoonRoute, sem guard. SITE_MAP: "Marketing" (status planejado).
  {
    path: "/painel/admin/marketing/",
    label: "Marketing",
    description: "Ações de marketing (em breve).",
    icon: "star",
    guard: { type: "none" },
  },
  // main.ts:1076 — sem guard. SITE_MAP: "Integrações" (seção Superadmin, status ativo).
  {
    path: "/painel/admin/integracoes/",
    label: "Integrações",
    description: "CRUD de integrações (eSolution, TSE, Sienge, eSolution Portal, UAU).",
    icon: "link",
    guard: { type: "none" },
  },
  // main.ts:1102/1109 — guarded("parametrizacoes.admin.boletos.view"). SITE_MAP: "Config Boletos".
  {
    path: "/painel/admin/boletos/",
    label: "Boletos",
    description: "Configuração de boletos do cotista.",
    icon: "file-text",
    guard: { type: "resource", key: "parametrizacoes.admin.boletos", opcao: "view" },
  },
  // main.ts:1121/1130 — guarded("parametrizacoes.admin.portal-regras.view"). SITE_MAP: "Regras eSolution Portal" (Admin/Backoffice).
  {
    path: "/painel/admin/portal-regras/",
    label: "Regras eSolution Portal",
    description: "Regras do portal por empreendimento.",
    icon: "settings-2",
    guard: { type: "resource", key: "parametrizacoes.admin.portal-regras", opcao: "view" },
  },
  // main.ts:1142/1146 — mesmo guard da rota de lista acima. SITE_MAP gap (detalhe dinâmico).
  {
    path: /^\/painel\/admin\/portal-regras\/\d+\/(reservas|pool|financeiro|limites|documento|termos|param)?\/?$/,
    label: "Regras eSolution Portal — Empreendimento",
    description: "Parametrização de reservas por empreendimento.",
    icon: "settings-2",
    guard: { type: "resource", key: "parametrizacoes.admin.portal-regras", opcao: "view" },
  },
  // main.ts:1158 — sem guard. SITE_MAP: "Meu Perfil" (seção Admin/Backoffice).
  {
    path: "/painel/admin/perfil/",
    label: "Meu Perfil",
    description: "Perfil do próprio admin.",
    icon: "user",
    guard: { type: "none" },
  },
  // main.ts:1179 — defineComingSoonRoute, sem guard. SITE_MAP: "Financeiro" (status planejado).
  {
    path: "/painel/admin/financeiro/",
    label: "Financeiro",
    description: "Módulo financeiro (em breve).",
    icon: "wallet",
    guard: { type: "none" },
  },
  // main.ts:1185 — defineComingSoonRoute, sem guard. SITE_MAP: "Relatórios" (status planejado).
  {
    path: "/painel/admin/relatorios/",
    label: "Relatórios",
    description: "Relatórios (em breve).",
    icon: "file-text",
    guard: { type: "none" },
  },
  // Plano "Mídia e uploads por módulo" (Fase 5): "/painel/admin/configuracoes/"
  // (antes `defineComingSoonRoute`) virou `defineInternalRedirect` para
  // "/painel/admin/integracoes/" — não é mais RouteEntry própria (mesmo
  // motivo de "/painel/superadmin/customizacao/" acima).
  // ── Mídia (plano "Midia e uploads por módulo", Fases 1/5) ────────────────
  // main.ts — guarded(codename("midia.admin.biblioteca", "view")). SITE_MAP: "Biblioteca de Mídia".
  {
    path: "/painel/admin/midia/biblioteca/",
    label: "Biblioteca de Mídia",
    description: "Arquivos de mídia da instância — busca, visualização e exclusão.",
    icon: "image",
    guard: { type: "resource", key: "midia.admin.biblioteca", opcao: "view" },
  },
  // main.ts — guarded(codename("midia.admin.biblioteca", "create")). SITE_MAP: "Upload de Mídia".
  {
    path: "/painel/admin/midia/upload/",
    label: "Upload de Mídia",
    description: "Envio de arquivos para um módulo de mídia (customizações ou empreendimentos).",
    icon: "upload",
    guard: { type: "resource", key: "midia.admin.biblioteca", opcao: "create" },
  },
  // main.ts:1203 — sem guard (nenhum item de nav aponta pra cá; freio real é o
  // guard de zona /painel/* em mountChrome). SITE_MAP gap (deliberado, sem link de nav).
  {
    path: "/painel/admin/_react-poc/",
    label: "React PoC",
    description: "Rota de prova de conceito, sem link de navegação.",
    icon: "layout-dashboard",
    guard: { type: "none" },
  },
  // ── User area ───────────────────────────────────────────────────────────
  // main.ts:1223/1230 — guarded("cotista.user.dashboard.view"). SITE_MAP: "Dashboard" (Portal do Cotista).
  {
    path: /^\/app\/dashboard\/?$/,
    label: "Dashboard",
    description: "Início do portal do cotista.",
    icon: "layout-dashboard",
    guard: { type: "resource", key: "cotista.user.dashboard", opcao: "view" },
  },
  // main.ts:1243/1250 — guarded("cotista.user.empreendimentos.view"). SITE_MAP: "Meus Empreendimentos".
  {
    path: /^\/app\/empreendimentos\/?$/,
    label: "Meus Empreendimentos",
    description: "Empreendimentos do cotista.",
    icon: "building-2",
    guard: { type: "resource", key: "cotista.user.empreendimentos", opcao: "view" },
  },
  // main.ts:1263/1275 — mesmo guard da rota de lista acima. SITE_MAP gap (detalhe dinâmico).
  {
    path: /^\/app\/empreendimentos\/(\d+)\/?$/,
    label: "Meus Empreendimentos — Detalhe",
    description: "Detalhe de um empreendimento do cotista.",
    icon: "building-2",
    guard: { type: "resource", key: "cotista.user.empreendimentos", opcao: "view" },
  },
  // main.ts:1292/1299 — guarded("hotelaria.user.pool-hoteleiro.view"). SITE_MAP: "Pool Hoteleiro" (Portal do Cotista).
  {
    path: /^\/app\/pool\/?$/,
    label: "Pool Hoteleiro",
    description: "Depósito e retirada do pool.",
    icon: "waves",
    guard: { type: "resource", key: "hotelaria.user.pool-hoteleiro", opcao: "view" },
  },
  // main.ts:1312/1319 — guarded("condominio.user.cotas.view"). SITE_MAP: "Cotas e Unidades".
  {
    path: /^\/app\/cotas\/?$/,
    label: "Cotas e Unidades",
    description: "Cotas, semanas e unidades.",
    icon: "building-2",
    guard: { type: "resource", key: "condominio.user.cotas", opcao: "view" },
  },
  // main.ts:1332/1336 — guarded("hotelaria.user.ocupacao.view"). SITE_MAP: "Ocupação" (Portal do Cotista).
  {
    path: /^\/app\/ocupacao\/?$/,
    label: "Ocupação",
    description: "Ocupação das unidades.",
    icon: "bed-double",
    guard: { type: "resource", key: "hotelaria.user.ocupacao", opcao: "view" },
  },
  // main.ts:1349/1353 — guarded("condominio.user.condominio.view"). SITE_MAP: "Condomínio".
  {
    path: /^\/app\/condominio\/?$/,
    label: "Condomínio",
    description: "Visão geral do condomínio.",
    icon: "building-2",
    guard: { type: "resource", key: "condominio.user.condominio", opcao: "view" },
  },
  // main.ts:1366/1370 — guarded("condominio.user.prestacao-contas.view"). SITE_MAP: "Prestação de Contas".
  {
    path: /^\/app\/prestacao-contas\/?$/,
    label: "Prestação de Contas",
    description: "Prestação de contas do condomínio.",
    icon: "file-text",
    guard: { type: "resource", key: "condominio.user.prestacao-contas", opcao: "view" },
  },
  // main.ts:1383/1395 — guarded("hotelaria.user.reservas.view"). SITE_MAP: "Minhas Reservas".
  {
    path: /^\/app\/reservas\/?$/,
    label: "Minhas Reservas",
    description: "Reservas do cotista e convidados.",
    icon: "calendar-check",
    guard: { type: "resource", key: "hotelaria.user.reservas", opcao: "view" },
  },
  // main.ts:1408/1416 — mesmo guard da rota acima. SITE_MAP: "Realizar Reserva".
  {
    path: /^\/app\/reservas\/nova\/?$/,
    label: "Realizar Reserva",
    description: "Wizard de criação de reserva (cota, semana, hóspedes).",
    icon: "calendar-check",
    guard: { type: "resource", key: "hotelaria.user.reservas", opcao: "view" },
  },
  // main.ts:1437/1445 — mesmo guard. SITE_MAP gap (detalhe dinâmico).
  {
    path: /^\/app\/reservas\/(\d+)\/?$/,
    label: "Minhas Reservas — Detalhe",
    description: "Gerenciamento de uma reserva.",
    icon: "calendar-check",
    guard: { type: "resource", key: "hotelaria.user.reservas", opcao: "view" },
  },
  // main.ts:1462/1466 — guarded("hotelaria.user.intercambio.view"). SITE_MAP: "Intercâmbio".
  {
    path: /^\/app\/intercambio\/?$/,
    label: "Intercâmbio",
    description: "Troca/intercâmbio de semanas.",
    icon: "arrow-right",
    guard: { type: "resource", key: "hotelaria.user.intercambio", opcao: "view" },
  },
  // main.ts:1479/1486 — guarded("cotista.user.financeiro.view"). SITE_MAP: "Extrato Financeiro".
  {
    path: /^\/app\/extrato\/?$/,
    label: "Extrato Financeiro",
    description: "Extrato financeiro do cotista.",
    icon: "wallet",
    guard: { type: "resource", key: "cotista.user.financeiro", opcao: "view" },
  },
  // main.ts:1499/1505 — guarded("cotista.user.boletos.view"). SITE_MAP: "Boletos/Faturas".
  {
    path: /^\/app\/boletos\/?$/,
    label: "Boletos/Faturas",
    description: "Boletos e segunda via.",
    icon: "file-text",
    guard: { type: "resource", key: "cotista.user.boletos", opcao: "view" },
  },
  // main.ts:1518/1525 — mesmo guard da rota acima (view complementar, sem permissão própria).
  // SITE_MAP: "Taxa de Condomínio".
  {
    path: /^\/app\/taxa-condominio\/?$/,
    label: "Taxa de Condomínio",
    description: "Boletos de taxa de condomínio.",
    icon: "file-text",
    guard: { type: "resource", key: "cotista.user.boletos", opcao: "view" },
  },
  // main.ts:1538/1542 — guarded("cotista.user.documentos.view"). SITE_MAP: "Documentos" (Portal do Cotista).
  {
    path: /^\/app\/documentos\/?$/,
    label: "Documentos",
    description: "Documentos disponíveis.",
    icon: "file-text",
    guard: { type: "resource", key: "cotista.user.documentos", opcao: "view" },
  },
  // main.ts:1555/1562 — guarded("cotista.user.perfil.view"). SITE_MAP: "Meu Perfil" (Portal do Cotista).
  {
    path: /^\/app\/perfil\/?$/,
    label: "Meu Perfil",
    description: "Perfil e dados do cotista.",
    icon: "user",
    guard: { type: "resource", key: "cotista.user.perfil", opcao: "view" },
  },
  // main.ts:1575/1586 — guarded("cotista.user.comunicados.view"). SITE_MAP gap
  // (não listada em "Portal do Cotista", achado desta onda).
  {
    path: /^\/app\/comunicados\/?$/,
    label: "Comunicados",
    description: "Comunicados enviados ao cotista.",
    icon: "mail",
    guard: { type: "resource", key: "cotista.user.comunicados", opcao: "view" },
  },
  // main.ts:1607 — sem guard, sem item de nav (Spike B, ADR-012 — rota de teste
  // isolada). SITE_MAP gap (deliberado).
  {
    path: "/__router-preview",
    label: "Router Preview (Spike B)",
    description: "Rota de teste isolada do Spike B (ADR-012), sem link de navegação.",
    icon: "layout-dashboard",
    guard: { type: "none" },
  },
];
